import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import NavButtons from "./NavButtons";
import Loader from "./Loader";
import { generateQRCode, downloadQRCode } from "../utils/QrGenerator";
import { FaQrcode, FaDownload, FaTimes } from "react-icons/fa";
import {
  ensureValidToken,
  isOfflineMode,
  OFFLINE_CACHE_KEYS,
  cacheData,
  getCachedData,
} from "../utils/authService";
import "../styles/CardDetails.css";

const CardDetails = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [activeTab, setActiveTab] = useState("web-holder");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [isMyCard, setIsMyCard] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [offline, setOffline] = useState(isOfflineMode());
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleClick = (message) => {
    alert(message);
  };

  const checkIfMyCard = async (cardId) => {
    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      const response = await axios.get("https://api.nexcard.co.in/api/cards/me/", {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      const myCards = response.data.results || [];
      const isOwnCard = myCards.some(card => card.id === cardId);
      setIsMyCard(isOwnCard);
    } catch (error) {
      console.error("Error checking card ownership:", error);
      setIsMyCard(false);
    }
  };

  const generateAndPreviewQR = async () => {
    if (!selectedCard) return;
    
    setGeneratingQR(true);
    try {
      const qrRedirectUrl = `${window.location.origin}/shared-card/${selectedCard.id}`;
      const qrDataUrl = await generateQRCode(qrRedirectUrl);
      
      if (qrDataUrl) {
        setQrImageUrl(qrDataUrl);
        setShowQRModal(true);
      } else {
        alert("Failed to generate QR code. Please try again.");
      }
    } catch (error) {
      console.error("Error generating QR code:", error);
      alert("Error generating QR code. Please try again.");
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrImageUrl) return;
    
    downloadQRCode(qrImageUrl, `nexcard-${selectedCard.full_name || 'business-card'}.png`);
  };

  const handleLocationClick = (mapsUrl) => {
    window.open(mapsUrl, '_blank');
  };

  const handlePhoneClick = (phoneNumber) => {
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    window.open(`tel:${cleanNumber}`);
  };

  const handleEmailClick = (email) => {
    window.open(`mailto:${email}`);
  };

  const detectPlatform = (url) => {
    if (!url) return null;
    
    const lowerUrl = url.toLowerCase();
    
    if (lowerUrl.includes('whatsapp.com') || lowerUrl.includes('wa.me')) return 'whatsapp';
    if (lowerUrl.includes('instagram.com')) return 'instagram';
    if (lowerUrl.includes('linkedin.com')) return 'linkedin';
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'twitter';
    if (lowerUrl.includes('github.com')) return 'github';
    if (lowerUrl.includes('facebook.com')) return 'facebook';
    
    return null;
  };

  const isPdfFile = (url) => {
    return url?.endsWith('.pdf') || url?.includes('.pdf?');
  };

  const handlePdfClick = () => {
    setShowPdfModal(true);
  };

  useEffect(() => {
    const fetchCardDetails = async () => {
      // Offline mode: read from cache and do not call APIs
      if (isOfflineMode()) {
        const cached = getCachedData(
          `${OFFLINE_CACHE_KEYS.cardDetailPrefix}${id}`,
          null
        );
        if (cached) {
          setSelectedCard(cached);
          setError(null);
        } else {
          setError("No cached data available for this card in offline mode.");
        }
        setOffline(true);
        setLoading(false);
        return;
      }

      try {
        const accessToken = await ensureValidToken();
        if (!accessToken) {
          navigate("/login");
          return;
        }
        const response = await axios.get(
          `https://api.nexcard.co.in/api/cards/${id}/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        setSelectedCard(response.data);
        cacheData(`${OFFLINE_CACHE_KEYS.cardDetailPrefix}${id}`, response.data);
        setOffline(false);

        // Check if this is user's own card (only when online)
        if (!isOfflineMode()) {
          await checkIfMyCard(id);
        }
      } catch (err) {
        console.error("Error fetching card details:", err);
        if (err.response?.status === 404) {
          setError("Card not found");
        } else {
          setError("Failed to load card details");
        }
      } finally {
        setLoading(false);
      }
    };
  
    fetchCardDetails();
    // eslint-disable-next-line
  }, [id, navigate]);

  // Clear QR image when modal closes
  useEffect(() => {
    if (!showQRModal) {
      // Keep a small delay to allow smooth transition
      const timer = setTimeout(() => {
        setQrImageUrl(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showQRModal]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!selectedCard) {
    return (
      <div className="not-found">
        <h1>Card not found</h1>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <Header toggleSidebar={toggleSidebar} />
      <NavButtons activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="business-card-app">
        <div className="content2">
          {offline && (
            <div className="offline-banner">
              You are in offline mode. Showing last saved card details.
            </div>
          )}
          <button className="back-btn2" onClick={() => navigate(-1)}>
            ← go back
          </button>

          {isMyCard && (
            <div className="qr-download-section">
              <button 
                className="qr-download-btn"
                onClick={generateAndPreviewQR}
                disabled={generatingQR}
              >
                {generatingQR ? (
                  <>
                    <Loader small={true} />
                    Generating QR...
                  </>
                ) : (
                  <>
                    <FaQrcode className="qr-icon" />
                    Preview & Download QR
                  </>
                )}
              </button>
              <p className="qr-help-text">
                Preview QR code before downloading to share your digital business card
              </p>
            </div>
          )}

          <div className="card-container2">
            {/* ... rest of your card display code remains the same ... */}
            <div className="card-left2">
              {isPdfFile(selectedCard.file?.file) ? (
                <div className="pdf-preview-container" onClick={handlePdfClick}>
                  <div className="pdf-preview">
                    <div className="pdf-icon">PDF</div>
                    <span>{selectedCard.file?.name || 'Business Card'}</span>
                    <div className="view-pdf-text">Click to view PDF</div>
                  </div>
                </div>
              ) : (
                <img
                  src={selectedCard.file?.file}
                  alt="Business Card"
                  className="card-image2"
                />
              )}
            </div>
            <div className="card-right2">
              <div className="profile-pic2">
                {selectedCard.profile_image ? (
                  <img src={selectedCard.profile_image} alt="Profile" />
                ) : (
                  <img
                    src="https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png"
                    alt="Default Profile"
                  />
                )}
              </div>
              <h2 className="card-info">{selectedCard.full_name}</h2>
              <p className="card-info">
                <strong>{selectedCard.designation}</strong>
              </p>
              <p className="card-info">{selectedCard.company_name}</p>
              
              <p 
                className="card-info clickable" 
                onClick={() => handlePhoneClick(selectedCard.phone_number)}
              >
                📞 {selectedCard.phone_number}
              </p>
              
              <p 
                className="card-info clickable" 
                onClick={() => handleEmailClick(selectedCard.email)}
              >
                📧 {selectedCard.email}
              </p>
              
              <p className="card-info">📍 {selectedCard.location_details}</p>

              <div className="social-links-container">
                {selectedCard.social_media_links && selectedCard.social_media_links.map((link, index) => {
                  if (!link) return null;
                  
                  // Check if it's a location link
                  if (link.includes('google.com/maps') || link.includes('maps.app.goo.gl')) {
                    return (
                      <button 
                        key={index}
                        className="location-link-btn"
                        onClick={() => handleLocationClick(link)}
                        title="Open location in Google Maps"
                      >
                        <i className="fas fa-map-marker-alt"></i>
                        <span>View Location</span>
                      </button>
                    );
                  }
                  
                  const platform = detectPlatform(link);
                  
                  return platform ? (
                    <a 
                      key={index} 
                      href={link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-icon-link"
                    >
                      <i className={`fab fa-${platform}`}></i>
                    </a>
                  ) : (
                    <a 
                      key={index} 
                      href={link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-link"
                    >
                      {link}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Preview Modal */}
      {showQRModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="qr-modal-close"
              onClick={() => setShowQRModal(false)}
            >
              <FaTimes />
            </button>
            
            <div className="qr-modal-header">
              <h3>QR Code for {selectedCard.full_name}</h3>
              <p className="qr-modal-subtitle">
                Scan this QR to view the digital business card
              </p>
            </div>
            
            <div className="qr-code-preview">
              {qrImageUrl ? (
                <img src={qrImageUrl} alt="QR Code" className="qr-image" />
              ) : (
                <div className="qr-placeholder">
                  <Loader small={true} />
                  <p>Loading QR code...</p>
                </div>
              )}
            </div>
            
            <div className="qr-modal-footer">
              {/* <p className="qr-info-text">
                The QR code points to: <br />
                <span className="qr-url">
                  {window.location.origin}/shared-card/{selectedCard.id}
                </span>
              </p> */}
              
              <div className="qr-modal-actions">
                <button 
                  className="qr-download-btn-modal"
                  onClick={handleDownloadQR}
                  disabled={!qrImageUrl}
                >
                  <FaDownload /> Download QR Code
                </button>
                <button 
                  className="qr-close-btn-modal"
                  onClick={() => setShowQRModal(false)}
                >
                  <FaTimes /> Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPdfModal && (
        <div className="pdf-modal-overlay" onClick={() => setShowPdfModal(false)}>
          <div className="pdf-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="pdf-modal-close"
              onClick={() => setShowPdfModal(false)}
            >
              ×
            </button>
            <iframe 
              src={selectedCard.file?.file} 
              className="pdf-iframe"
              title="PDF Viewer"
            ></iframe>
          </div>
        </div>
      )}

      <Footer handleClick={handleClick} />
    </>
  );
};

export default CardDetails;